import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as React from 'react';

import { cn } from '@/utils/cn';

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = TooltipPrimitive.Trigger;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
  <TooltipPrimitive.Content
    ref={ref}
    sideOffset={sideOffset}
    className={cn(
      'z-50 overflow-hidden rounded-md bg-foreground px-3 py-1.5 text-xs text-primary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
      className,
    )}
    {...props}
  />
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

const TruncateTextWithTooltip: React.FC<{
  text?: string;
  className?: string;
}> = ({ text, className }) => {
  const textRef = React.useRef<HTMLDivElement>(null);
  const [isOverflow, setIsOverflow] = React.useState(false);

  React.useEffect(() => {
    const checkOverflow = () => {
      const { current } = textRef;
      if (current) {
        setIsOverflow(current.scrollWidth > current.clientWidth);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    return () => {
      window.removeEventListener('resize', checkOverflow);
    };
  }, [text]);

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            ref={textRef}
            className={cn('truncate', className)}
            style={{ maxWidth: '100%' }}
          >
            {text}
          </div>
        </TooltipTrigger>
        {isOverflow && (
          <TooltipContent>
            <span>{text}</span>
          </TooltipContent>
        )}
      </Tooltip>
    </TooltipProvider>
  );
};

export {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
  TruncateTextWithTooltip,
};
